import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronDown, Droplets } from 'lucide-react';
import { toast } from 'sonner';
import { calculatePricing } from '../services/pricingService';

const GutterCalculatorPanel = forwardRef(({
  isOpen, onToggle, measurementTotals, manualOverride, setManualOverride,
  onServiceTotalChange,
  initialInputs,          // Tier 2: parent-cached inputs restored on remount
  onInputsChange,         // Tier 2: push current inputs up to parent mirror for staleness
}, ref) => {
  const [gutterData, setGutterData] = useState(
    initialInputs && Object.keys(initialInputs).length
      ? { linearFeet: '', stories: '1', ...initialInputs }
      : { linearFeet: '', stories: '1' }
  );

  // Tier 2: mirror current inputs to parent on every change.
  const onInputsChangeRef = useRef(onInputsChange);
  onInputsChangeRef.current = onInputsChange;
  useEffect(() => {
    onInputsChangeRef.current?.('gutter', gutterData);
  }, [gutterData]);

  // Auto-fill from measurement if panel is open and user hasn't manually edited
  useEffect(() => {
    if (!isOpen) return;
    const feet = measurementTotals?.lineFeet || 0;
    // Only auto-fill if: feet > 0 AND user hasn't manually overridden
    if (feet > 0 && !manualOverride.gutterFt) {
      // Round to whole number for clean UX display
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional sync from external measurement source
      setGutterData((prev) => ({ ...prev, linearFeet: Math.round(feet) }));
    }
  }, [isOpen, measurementTotals?.lineFeet, manualOverride.gutterFt]);

  const handleCalculate = async () => {
    const lf = Number(gutterData.linearFeet);
    if (!Number.isFinite(lf) || lf <= 0) {
      toast.error('Please enter gutter linear feet.');
      return;
    }

    const resp = await calculatePricing('gutter', {
      data: {
        linearFeet: lf,
        stories: gutterData.stories,
      },
      discount: 0,
    });

    if (!resp.success || !resp.quote) {
      toast.error(resp.message || 'Service pricing not implemented yet, but UI remains active.');
      onServiceTotalChange('gutter', { subtotal: 0, tax: 0, grandTotal: 0 });
      return;
    }

    const quote = resp.quote;
    const nowIso = new Date().toISOString();

    onServiceTotalChange('gutter', {
      input: {
        linearFeet: lf,
        stories: gutterData.stories,
      },
      result: {
        serviceTotal: quote.serviceTotal || quote.subtotal || 0,
        adjustedFeet: quote.adjustedFeet || lf,
        discount: quote.discount || 0,
        tax: quote.tax || 0,
        subtotal: quote.subtotal || quote.serviceTotal || 0,
        grandTotal: quote.grandTotal || 0,
      },
      measurement: {
        polygons: null,
        linearFeet: lf,
        sqFt: null,
        computedAt: nowIso,
      },
    });
  };

  // Tier 2: expose imperative recalc so parent's Recalculate All Stale can fire.
  useImperativeHandle(ref, () => ({
    recalc: handleCalculate,
    getCurrentInputs: () => gutterData,
  }), [gutterData]);

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <Card className="overflow-hidden">
        <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-secondary/10 rounded-lg">
              <Droplets className="h-5 w-5 text-secondary" />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-bold text-secondary">💧 Gutter Cleaning</h3>
              <p className="text-sm text-muted-foreground">Configure gutter specifications</p>
            </div>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-muted-foreground transition-transform ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="p-6 space-y-4 bg-muted/30">
            {/* Linear Feet */}
            <div className="space-y-2">
              <Label htmlFor="gutter-length">Linear Feet</Label>
              <Input
                id="gutter-length"
                type="number"
                placeholder="Enter linear feet"
                className="font-mono"
                value={gutterData.linearFeet}
                onChange={(e) => {
                  setManualOverride(prev => ({ ...prev, gutterFt: true }));
                  setGutterData(prev => ({ ...prev, linearFeet: e.target.value }));
                }}
              />
            </div>

            {/* Gutter Type */}
            <div className="space-y-2">
              <Label htmlFor="gutter-type">Gutter Type</Label>
              <Select>
                <SelectTrigger id="gutter-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="oversized">Oversized</SelectItem>
                  <SelectItem value="covered">Covered/Protected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Stories */}
            <div className="space-y-2">
              <Label htmlFor="gutter-stories">Stories</Label>
              <Select
                value={gutterData.stories}
                onValueChange={(value) => setGutterData(prev => ({ ...prev, stories: value }))}
              >
                <SelectTrigger id="gutter-stories">
                  <SelectValue placeholder="Select stories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Story</SelectItem>
                  <SelectItem value="2">2 Stories</SelectItem>
                  <SelectItem value="3">3 Stories</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Discount removed at service level; handled in Quote Summary */}

            {/* Calculate Button */}
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              size="lg"
              onClick={handleCalculate}
            >
              Calculate Gutter Cost
            </Button>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
});

export default GutterCalculatorPanel;
